import { useTranslation } from 'react-i18next';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { StatusChip } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import { addDaysToDay } from '../../../utils/task-date';
import { DayTarget, formatHours, loggedHours } from '../../hooks/use-under-time';
import { TimeSheetRecord, TimeSheetTask } from '../../types/timesheet.types';

type TimesheetWeekViewProps = {
  /** The first of the 7 days ("YYYY-MM-DD"). */
  startDay: string;
  /** The entries of those days. */
  records: TimeSheetRecord[];
  today: string;
  isLoading: boolean;
  dayTarget: (day: string, tasks: readonly TimeSheetTask[]) => DayTarget | null;
  onEdit: (record: TimeSheetRecord) => void;
  onDelete: (record: TimeSheetRecord) => void;
};

/** A day's status chip: Under time (destructive) or On target (success). */
export const DayTargetChip = ({ target }: { target: DayTarget | null }) => {
  const { t } = useTranslation();
  if (!target) return null;
  return target === 'under' ? (
    <StatusChip tone="error">{t('MY_TIMESHEET_UNDER_TIME')}</StatusChip>
  ) : (
    <StatusChip tone="success">{t('MY_TIMESHEET_ON_TARGET')}</StatusChip>
  );
};

/**
 * The Time Tracker's Week view (HR Module Prototype, Component Map › Time Tracker): seven day columns,
 * each with its tasks (project, customer, billable and non-billable hours), the day's total and its
 * status; a day under its working hours has a red header, today's name is in the primary colour. Edit
 * and Delete sit in the header of a day with an entry. Scrolls sideways on narrow screens.
 */
export const TimesheetWeekView = ({
  startDay,
  records,
  today,
  isLoading,
  dayTarget,
  onEdit,
  onDelete,
}: TimesheetWeekViewProps) => {
  const { t, i18n } = useTranslation();
  const days = Array.from({ length: 7 }, (_, index) => addDaysToDay(startDay, index));
  const byDay = new Map(records.map((record) => [record.TaskDate.slice(0, 10), record]));
  const label = (day: string, options: Intl.DateTimeFormatOptions) =>
    new Date(`${day}T00:00:00Z`).toLocaleDateString(i18n.language, { timeZone: 'UTC', ...options });

  return (
    <div className="grid grid-cols-[repeat(7,minmax(120px,1fr))] overflow-x-auto rounded-lg border bg-card">
      {days.map((day) => {
        const record = byDay.get(day);
        const tasks = record?.Tasks ?? [];
        const target = record ? dayTarget(day, tasks) : null;
        const isUnder = target === 'under';
        return (
          <section
            key={day}
            aria-label={label(day, { weekday: 'long', day: 'numeric', month: 'long' })}
            className="flex min-h-[220px] flex-col border-r last:border-r-0"
          >
            <div
              className={cn(
                'flex items-start justify-between gap-1 border-b px-2.5 py-2',
                isUnder && 'bg-destructive-subtle text-destructive-subtle-foreground'
              )}
            >
              <div className="grid">
                <b className={cn('font-semibold', day === today && !isUnder && 'text-primary')}>
                  {label(day, { weekday: 'short' })}
                </b>
                <span className={cn('text-xs tabular-nums', !isUnder && 'text-muted-foreground')}>
                  {label(day, { day: 'numeric', month: 'short' })}
                </span>
              </div>
              {record && (
                <span className="-mr-1.5 -mt-1 inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-[26px]"
                    aria-label={t('EDIT')}
                    title={t('EDIT')}
                    onClick={() => onEdit(record)}
                  >
                    <PencilSimple className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-[26px]"
                    aria-label={t('DELETE')}
                    title={t('DELETE')}
                    onClick={() => onDelete(record)}
                  >
                    <Trash className="size-3.5" />
                  </Button>
                </span>
              )}
            </div>

            <div className="grid flex-1 content-start gap-1.5 p-2">
              {isLoading && <Skeleton className="h-16 w-full" />}
              {tasks.map((task, index) => (
                <div key={`${task.MbmProjectId}-${index}`} className="grid gap-0.5 rounded-md border p-2 text-xs">
                  <b className="truncate text-[13px] font-semibold">{task.ProjectName ?? '—'}</b>
                  <span className="truncate text-muted-foreground">{task.CustomerName ?? '—'}</span>
                  <span className="flex justify-between gap-1.5">
                    <span className="text-muted-foreground">{t('BILLABLE')}</span>
                    <b className="font-medium tabular-nums">{formatHours(task.Billable)}</b>
                  </span>
                  <span className="flex justify-between gap-1.5">
                    <span className="text-muted-foreground">{t('MY_TIMESHEET_NON_BILLABLE')}</span>
                    <b className="font-medium tabular-nums">{formatHours(task.NonBillable)}</b>
                  </span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-1 border-t px-2.5 py-1.5 text-xs text-muted-foreground">
              <span>
                {t('TOTAL')}{' '}
                <b className="font-semibold tabular-nums text-foreground">
                  {formatHours(loggedHours(tasks))}
                </b>
              </span>
              <DayTargetChip target={target} />
            </div>
          </section>
        );
      })}
    </div>
  );
};
